import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import type { ToastData, ToastType } from '@/components/ui/Toast'

/**
 * useToast Hook
 *
 * Uses React Context so all components share a single global toast state.
 * The ToastProvider renders the ToastContainer at the app root.
 *
 * Features:
 * - Auto-dismiss with configurable duration
 * - Multiple toasts stacking
 * - Animated entry/exit states
 * - Global context for app-wide access
 * - Extended types: success, error, warning, info, alert, system
 */

export interface ToastState extends ToastData {
  isVisible: boolean
}

export interface ToastOptions {
  actionText?: string
  actionHref?: string
  actionOnClick?: () => void
}

export interface ToastContextValue {
  toasts: ToastState[]
  show: (type: ToastType, title: string, description?: string, duration?: number, options?: ToastOptions) => string
  dismiss: (id: string) => void
  success: (title: string, description?: string, duration?: number, options?: ToastOptions) => string
  error: (title: string, description?: string, duration?: number, options?: ToastOptions) => string
  warning: (title: string, description?: string, duration?: number, options?: ToastOptions) => string
  info: (title: string, description?: string, duration?: number, options?: ToastOptions) => string
  alert: (title: string, description?: string, duration?: number, options?: ToastOptions) => string
  system: (title: string, description?: string, duration?: number, options?: ToastOptions) => string
}

const DEFAULT_DURATION = 5000 // 5 seconds

export const ToastContext = createContext<ToastContextValue | null>(null)

/**
 * Hook that provides toast state management.
 * Used internally by ToastProvider.
 */
export function useToastState(): ToastContextValue {
  const [toasts, setToasts] = useState<ToastState[]>([])
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
    }
  }, [])

  const dismiss = useCallback((id: string) => {
    // First, set isVisible to false for exit animation
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, isVisible: false } : toast
      )
    )

    // Clear any existing timer for this toast
    const existingTimer = timersRef.current.get(id)
    if (existingTimer) {
      clearTimeout(existingTimer)
      timersRef.current.delete(id)
    }

    // Remove from DOM after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id))
    }, 200) // Match exit animation duration
  }, [])

  const show = useCallback(
    (
      type: ToastType,
      title: string,
      description?: string,
      duration: number = DEFAULT_DURATION,
      options?: ToastOptions
    ) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

      const newToast: ToastState = {
        id,
        type,
        title,
        description,
        duration,
        actionText: options?.actionText,
        actionHref: options?.actionHref,
        actionOnClick: options?.actionOnClick,
        isVisible: true,
      }

      setToasts((prev) => [...prev, newToast])

      // Set auto-dismiss timer
      if (duration > 0) {
        const timer = setTimeout(() => {
          dismiss(id)
        }, duration)
        timersRef.current.set(id, timer)
      }

      return id
    },
    [dismiss]
  )

  // Convenience methods for each toast type
  const success = useCallback(
    (title: string, description?: string, duration?: number, options?: ToastOptions) =>
      show('success', title, description, duration, options),
    [show]
  )

  const error = useCallback(
    (title: string, description?: string, duration?: number, options?: ToastOptions) =>
      show('error', title, description, duration, options),
    [show]
  )

  const warning = useCallback(
    (title: string, description?: string, duration?: number, options?: ToastOptions) =>
      show('warning', title, description, duration, options),
    [show]
  )

  const info = useCallback(
    (title: string, description?: string, duration?: number, options?: ToastOptions) =>
      show('info', title, description, duration, options),
    [show]
  )

  const alert = useCallback(
    (title: string, description?: string, duration?: number, options?: ToastOptions) =>
      show('alert', title, description, duration, options),
    [show]
  )

  const system = useCallback(
    (title: string, description?: string, duration?: number, options?: ToastOptions) =>
      show('system', title, description, duration, options),
    [show]
  )

  return {
    toasts,
    show,
    dismiss,
    success,
    error,
    warning,
    info,
    alert,
    system,
  }
}

/**
 * Hook to access the global toast context.
 * Must be used within a ToastProvider.
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
